import { action, observable, computed, isObservableArray, toJS, makeObservable } from 'mobx';
import * as api from './api';
import {keyBy} from 'lodash';
import LoginStore from "../session/LoginStore";
import * as mobx from 'mobx';
import { observer } from 'mobx-react';

class ProvidersStore {
  //TODO - cache dictionary by Type

  /**
   * @typedef {object} ContentProvider
   * @property {string} id
   * @property {string} name
   * @property {string} urlValidation?
   * @property {string} exampleURL?
   * @property {string} imageUrl?
   * @property {{
   *   listings: boolean
   *   listingManagement: boolean
   *   reviews: boolean
   *   hideReviewSentiment: boolean
   *   reviewResponses: boolean
   *   reviewRatings: boolean
   *   webRanking: boolean
   *   localRanking: boolean
   *   chatter: boolean
   *   media: boolean
   *   reviewsAreAllSyndicated: boolean
   * }} dashboardSettings,
   * @property {{
   *   supportsFixedBusinessId: boolean
   *   publicProvider: boolean
   *   isReviewBuilder: boolean
   *   shareableReviews: boolean
   *   hidden: boolean
   *   supportsOauth: boolean
   *   reviewBuilderAllowed: boolean
   *   realTimeReviews: boolean
   *   canRespondToReviews: boolean
   * }} providerSettings
   * @property {{
   *   displaysCategories: boolean
   *   displaysMultipleCategories: boolean
   *   displaysImages: boolean
   *   displaysPhoneNumber: boolean
   *   displaysWebsite: boolean
   *   displaysListingUrl: boolean
   *   displaysDescription: boolean
   *   supportsEditFixedUrl: boolean
   * }} displaySettings
   */

  static SocialProviders = [
    'FACEBOOK',
    'TWITTER',
    'INSTAGRAM',
    'GOOGLE',
    'LINKEDIN'
  ];

  static PageInsightsProviders = [
    'GOOGLE',
    'YELP'
  ]

  static ProviderType = {
    REVIEWS: 'REVIEWS',
    PULSE: 'PULSE',
    REVIEW_RESPONSES: 'REVIEW_RESPONSES',
    LISTINGS: 'LISTINGS',
    LISTING_MANAGEMENT: 'LISTING_MANAGEMENT'
  };

  /**
   *
   * @type {Array.<ContentProvider>}
   */
  providers = [];

  loaded = false;

  getProvider(id) {
    return this.providers.find(p => p.id === id)
  }

  displayNameFor(id){
    const found = this.providers.find(x =>
      x.id === id.toUpperCase() || (id.toUpperCase() === 'GOOGLEMAP' && x.id === 'GOOGLE')
    );
    if (!found) {
      return id.charAt(0).toUpperCase() + id.slice(1).toLowerCase();
    } else {
      return found.name;
    }
  }

  constructor() {
    makeObservable(this, {
      providers: observable,
      loaded: observable,
      socialProviders: computed,
      reviewProviders: computed,
      reviewBuilderRevGenProviders: computed,
      pulseProviders: computed,
      reviewResponseProviders: computed,
      listingsProviders: computed,
      webRankProviders: computed,
      localRankProviders: computed,
      mediaProviders: computed,
      pageInsightsProviders: computed,
      _validPageInsightsProviders: observable,
      validPageInsightsProviders: computed,
      setValidPageInsightsProviders: action,
      byId: computed
    });

    mobx.reaction(
      () => LoginStore.isLoggedIn,
      token => {
        if (token) {
          this._getProviders();
        } else {
          mobx.runInAction(() => {
            this.loaded = false;
            this.providers.splice(0);
          });
        }
      }, {
        fireImmediately: true
      }
    )
  }

  _getProviders = () => {
    const getProviders = api.getProviders(true);
    LoginStore.runUsingCachedResult({
      promise: getProviders,
      callback: action('onProvidersLoaded', (providers) => {
        this.providers.splice(0);
        this.providers.push(...providers);
        this.loaded = true;
      }),
      key: "ProviderStore.providers"
    });
  };

  get socialProviders() {
    return this.providers.filter(p => ProvidersStore.SocialProviders.indexOf(p.id) !== -1);
  }
  get reviewProviders() {
    return this.providers.filter(p => p.dashboardSettings.reviews);
  }
  get reviewBuilderRevGenProviders() {
    return this.providers.filter(p => p.providerSettings.reviewBuilderAllowed);
  }
  get pulseProviders() {
    return this.providers.filter(p => {
      return p.dashboardSettings.reviews && !p.dashboardSettings.hideReviewSentiment
    });
  }
  get reviewResponseProviders() {
    return this.providers.filter(p => p.providerSettings.canRespondToReviews);
  }
  get listingsProviders() {
    return this.providers.filter(p => p.dashboardSettings.listings);
  }

  get webRankProviders() {
    return this.providers.filter(p => p.dashboardSettings.webRanking)
  }

  get localRankProviders() {
    return this.providers.filter(p => p.dashboardSettings.localRanking)
  }

  get mediaProviders() {
    return this.providers.filter(p => p.dashboardSettings.media)
  }

  get oauthProviders() {
    return this.providers.filter(p => p.providerSettings.supportsOauth)
  }

  get pageInsightsProviders() {
    return this.providers.filter(p => ProvidersStore.PageInsightsProviders.indexOf(p.id) !== -1);
  }

  _validPageInsightsProviders = [];

  get validPageInsightsProviders() {
    return this._validPageInsightsProviders;
  }

  setValidPageInsightsProviders() {
    return api.getSearchAnalyticsProviders().then(data => {
      const providerIds = data.providers.map(x => x.toUpperCase());
      const filteredProviders = this.providers.filter(p => providerIds.indexOf(p.id) !== -1);
      this._validPageInsightsProviders = [...filteredProviders];
    });
  }

  get byId() {
    const idx = {}
    for (let p of this.providers) {
      idx[p.id] = p
    }
    return idx
  }

  validProviders(groupId, type = ProvidersStore.ProviderType.REVIEWS) {
    return api.getProvidersByGroupAndType(groupId, type).then(response => response.data);
  }

  //To only return valid providers as a promise instead of data for listing accuracy
  validProvidersData(groupId, type = ProvidersStore.ProviderType.REVIEWS) {
    return api.getProvidersByGroupAndType(groupId, type);
  }

  // TODO: Need to fix this: it's fetching only the selected type, which can potentially mess
  // with other components that need a different type.
  validProvidersAndKeyedById(groupId, type = ProvidersStore.ProviderType.REVIEWS) {
    if (this.providers.length && this.type === type) {
      return Promise.resolve(this.providers);
    } else {
      return this.validProviders(groupId, type)
        .then(providers => ({providers, providersById: keyBy(providers, 'id')}))
        .then(() => this.type = type);
    }
  }


}

export default new ProvidersStore();
